import React, { Component } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
class TransportDashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      chartOptions: { title: { text: 'Loading...' }, series: [] },
      selectedMetric: 'Avg Dead Miles(KM)',
      selectedTransporters: [],
      availableMetrics: [
        'Total Trucks',
        'Avg Dead Miles(KM)',
        'Other OEM Load Not Taken (%)',
        'Other OEM Load Taken (%)'
      ],
      error: null,
      showTransportersDropdown: false,
      transporterSearchQuery: '' // New state for search functionality
    };
    this.dropdownRef = React.createRef();
  }
  // Add the search handler method
  handleSearchChange = (e) => {
    this.setState({ transporterSearchQuery: e.target.value });
  };
  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
    this.initializeDashboard();
  }
  componentDidUpdate(prevProps) {
    if (prevProps.data !== this.props.data) {
      this.initializeDashboard();
    }
  }
  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }
  handleClickOutside = (event) => {
    if (this.dropdownRef.current &&
      !this.dropdownRef.current.contains(event.target)) {
      this.setState({ showTransportersDropdown: false });
    }
  };
  initializeDashboard = () => {
    try {
      const data = this.props.data || [];
      if (data.length > 0) {
        const allTransporters = this.getAllTransporters(data);
        const initialTransporters = allTransporters;
        this.setState({ selectedTransporters: initialTransporters }, this.prepareChartOptions);
      }
    } catch (error) {
      this.setState({ error: 'Failed to initialize dashboard' });
      console.error('Initialization error:', error);
    }
  }
  getDates = (data) => {
    if (!data || data.length === 0) return [];

    const dateKeys = new Set();
    const monthAbbreviations = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
      'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

    data.forEach(item => {
      Object.keys(item).forEach(key => {
        const dateMatch = key.match(/^([A-Za-z]{3})-(\d{4})$/);
        if (dateMatch) {
          const month = dateMatch[1].toUpperCase();
          const year = dateMatch[2];
          dateKeys.add(`${month}-${year}`);
        }
      });
    });
    return Array.from(dateKeys).sort((a, b) => {
      const [aMonth, aYear] = a.split('-');
      const [bMonth, bYear] = b.split('-');
      return aYear - bYear || monthAbbreviations.indexOf(aMonth) - monthAbbreviations.indexOf(bMonth);
    });
  };
  getAllTransporters = (data) => {
    if (!data) return [];
    const transporters = new Set();
    data.forEach(item => {
      if (item.transporter && item.transporter.trim() !== '') {
        transporters.add(item.transporter);
      }
    });
    return Array.from(transporters).sort();
  };
  // Add method to filter transporters based on search query
  getFilteredTransporters = () => {
    const { transporterSearchQuery } = this.state;
    const allTransporters = this.getAllTransporters(this.props.data || []);
    if (!transporterSearchQuery.trim()) {
      return allTransporters;
    }
    const query = transporterSearchQuery.toLowerCase();
    return allTransporters.filter(transporter =>
      transporter.toLowerCase().includes(query)
    );
  };
  prepareChartOptions = () => {
    try {
      const data = this.props.data || [];
      const { selectedMetric, selectedTransporters } = this.state;
      if (data.length === 0) {
        return this.setState({
          chartOptions: {
            title: { text: 'No data available' },
            series: []
          }
        });
      }
      if (selectedTransporters.length === 0) {
        return this.setState({
          chartOptions: {
            title: { text: 'No transporters selected' },
            series: []
          }
        });
      }
      const dates = this.getDates(data).map(date => {
        const [month, year] = date.split('-');
        return `${month.charAt(0)}${month.slice(1).toLowerCase()}-${year}`;
      });
      const seriesData = selectedTransporters.map(transporter => {
        const transporterRecords = data.filter(
          item => item.transporter === transporter &&
            item.parameters === selectedMetric
        );
        if (transporterRecords.length === 0) return null;
        const dataPoints = dates.map(date => {
          const record = transporterRecords.find(r => r[date] !== undefined);
          return record ? Number(record[date]) : null;
        });
        return {
          name: transporter,
          data: dataPoints,
          type: 'spline',
          showInLegend: true
        };
      }).filter(series => series !== null);
      if (seriesData.length === 0) {
        return this.setState({
          chartOptions: {
            title: { text: 'No data available for selected metric' },
            series: []
          }
        });
      }
      this.setState({
        chartOptions: {
          chart: {
            type: 'spline',
            height: 500,
            spacingBottom: 20,
            spacingTop: 20,
            spacingLeft: 10,
            spacingRight: 10
          },
          title: {
            text: `${selectedMetric} by Transporter`,
            margin: 25
          },
          xAxis: {
            categories: dates,
            title: { text: 'Month' },
            labels: {
              rotation: -45,
              y: 20
            }
          },
          yAxis: {
            title: { text: selectedMetric },
            min: 0,
            endOnTick: false
          },
          series: seriesData,
          plotOptions: {
            series: {
              marker: { radius: 4 },
              lineWidth: 2,
              states: {
                hover: {
                  lineWidth: 3
                }
              }
            }
          },
          tooltip: {
            shared: true,
            crosshairs: true,
            formatter: function () {
              const points = this.points || [];
              const rows = points.map(function (point) {
                return `${point.series.name}: <b>${point.y}</b>`;
              });
              return `<b>${this.x}</b><br/>${rows.join('<br/>')}`;
            }
          },
          legend: {
            layout: 'vertical',
            align: 'right',
            verticalAlign: 'middle',
            itemMarginBottom: 5,
            symbolHeight: 12,
            symbolWidth: 12,
            symbolRadius: 0
          },
          credits: { enabled: false }
        },
        error: null
      });
    } catch (error) {
      console.error('Error preparing chart:', error);
      this.setState({
        error: 'Error preparing chart',
        chartOptions: {
          title: { text: 'Error loading chart' },
          series: []
        }
      });
    }
  };
  handleMetricChange = (e) => {
    this.setState({ selectedMetric: e.target.value }, () => {
      this.prepareChartOptions()
    });
  };
  toggleTransportersDropdown = () => {
    this.setState(prev => ({
      showTransportersDropdown: !prev.showTransportersDropdown,
      transporterSearchQuery: '' // Reset search when dropdown is toggled
    }));
  };
  handleTransporterChange = (transporter, e) => {
    const checked = e.target.checked;
    this.setState(prev => ({
      selectedTransporters: checked
        ? [...prev.selectedTransporters, transporter]
        : prev.selectedTransporters.filter(t => t !== transporter)
    }), this.prepareChartOptions);
  };
  handleSelectAll = (e) => {
    const checked = e.target.checked;
    const data = this.props.data || [];
    const allTransporters = this.getAllTransporters(data);
    this.setState({
      selectedTransporters: checked ? [...allTransporters] : []
    }, this.prepareChartOptions);
  };
  render() {
    const data = this.props.data || [];
    const {
      selectedMetric,
      selectedTransporters,
      chartOptions,
      availableMetrics,
      error,
      showTransportersDropdown,
      transporterSearchQuery
    } = this.state;
    const allTransporters = this.getAllTransporters(data);
    const filteredTransporters = this.getFilteredTransporters();
    return (
      <div style={{ padding: '20px 0px', margin: '0px' }}>
        {error && (
          <div style={{
            color: 'red',
            padding: '10px',
            marginBottom: '15px',
            border: '1px solid red',
            borderRadius: '4px',
            background: '#ffeeee'
          }}>
            {error}
          </div>
        )}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '20px',
          marginBottom: '20px',
          padding: '15px',
          background: '#f8f9fa',
          borderRadius: '6px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', minWidth: '300px' }}>
            <label style={{ marginRight: '10px', fontWeight: '500' }}>Metric:</label>
            <select
              value={selectedMetric}
              onChange={this.handleMetricChange}
              style={{
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid #ced4da',
                flex: 1,
                fontSize: '14px'
              }}
            >
              {availableMetrics.map(metric => (
                <option key={metric} value={metric}>{metric}</option>
              ))}
            </select>
          </div>
          <div ref={this.dropdownRef} style={{ position: 'relative', minWidth: '300px' }}>
            <button
              onClick={this.toggleTransportersDropdown}
              style={{
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid #ced4da',
                background: 'white',
                cursor: 'pointer',
                width: '100%',
                textAlign: 'left',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <span>
                {selectedTransporters.length === allTransporters.length && allTransporters.length > 0
                  ? 'All Transporters'
                  : `Transporters (${selectedTransporters.length} selected)`}
              </span>
              <span>{showTransportersDropdown ? '▲' : '▼'}</span>
            </button>
            {showTransportersDropdown && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                zIndex: 1000,
                background: 'white',
                border: '1px solid #ced4da',
                borderRadius: '0 0 4px 4px',
                maxHeight: '300px',
                overflowY: 'auto',
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
              }}>
                {/* Search input */}
                <div style={{
                  padding: '8px 12px',
                  position: 'sticky',
                  top: 0,
                  background: 'white',
                  zIndex: 1001,
                  borderBottom: '1px solid #eee'
                }}>
                  <input
                    type="text"
                    placeholder="Search transporters..."
                    value={transporterSearchQuery}
                    onChange={this.handleSearchChange}
                    style={{
                      width: '100%',
                      padding: '6px 10px',
                      borderRadius: '4px',
                      border: '1px solid #ced4da',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                {/* Select All option */}
                <div style={{
                  padding: '8px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  borderBottom: '1px solid #eee',
                  background: '#f8f9fa'
                }}>
                  <input
                    type="checkbox"
                    id="select-all-transporters"
                    checked={selectedTransporters.length === allTransporters.length && allTransporters.length > 0}
                    onChange={this.handleSelectAll}
                    style={{ marginRight: '10px' }}
                  />
                  <label
                    htmlFor="select-all-transporters"
                    style={{
                      cursor: 'pointer',
                      fontWeight: '500',
                      flex: 1
                    }}
                  >
                    Select All
                  </label>
                </div>

                {/* Filtered transporter list */}
                {filteredTransporters.length > 0 ? (
                  filteredTransporters.map(transporter => (
                    <div
                      key={transporter}
                      style={{
                        padding: '8px 12px',
                        display: 'flex',
                        alignItems: 'center',
                        borderBottom: '1px solid #eee'
                      }}
                    >
                      <input
                        type="checkbox"
                        id={`transporter-${transporter}`}
                        checked={selectedTransporters.includes(transporter)}
                        onChange={(e) => this.handleTransporterChange(transporter, e)}
                        style={{ marginRight: '10px' }}
                      />
                      <label
                        htmlFor={`transporter-${transporter}`}
                        style={{
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          flex: 1
                        }}
                      >
                        {transporter}
                      </label>
                    </div>
                  ))
                ) : (
                  <div style={{
                    padding: '12px',
                    textAlign: 'center',
                    color: '#6c757d'
                  }}>
                    No transporters found matching "{transporterSearchQuery}"
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div style={{
          background: 'white',
          borderRadius: '6px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          padding: '15px'
        }}>
          <HighchartsReact
            highcharts={Highcharts}
            options={chartOptions}
            containerProps={{ style: { width: '100%' } }}
          />
        </div>
      </div>
    );
  }
}

export default TransportDashboard;